import React from "react";
import { ChevronDown, Landmark, ScrollText, CreditCard, BadgeCheck } from "lucide-react";

// Completed-state (all 10 requirements met) presentation for the Verification page.
// Pure presentation over the payloads the page already holds — no fetching here.

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null);
const pillCls = "inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 shrink-0";
const summaryCls = "flex items-center gap-3 cursor-pointer list-none [&::-webkit-details-marker]:hidden min-w-0";
const chevron = <ChevronDown size={16} className="text-slate-400 transition-transform group-open:rotate-180 shrink-0" />;

export function CompactDocRow({ testKey, label, icon: Icon, status, doc, masked, hint }) {
  const dl = doc?.source === "digilocker" ? doc : null;
  const details = [
    masked && ["On record", masked],
    dl && ["Source", `DigiLocker${dl.digilocker?.issuer ? ` · ${dl.digilocker.issuer}` : ""}`],
    dl?.digilocker?.fetched_at && ["Fetched", fmtDate(dl.digilocker.fetched_at)],
    !dl && doc?.uploaded_at && ["Uploaded", fmtDate(doc.uploaded_at)],
    doc?.reviewed_at && ["Approved on", fmtDate(doc.reviewed_at)],
    hint && ["About", hint],
  ].filter(Boolean);
  return (
    <details className="group bg-white border border-slate-200 rounded-xl px-3 py-2.5" data-testid={`kyc-doc-${testKey}`}>
      <summary className={summaryCls}>
        <span className="h-8 w-8 rounded-lg bg-[#FBF4DF] text-[#0A2540] grid place-items-center shrink-0"><Icon size={15} /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-[#0A2540] truncate">{label}</span>
          <span className="block text-xs text-slate-500 truncate" data-testid={`kyc-doc-${testKey}-sub`}>{dl ? "DigiLocker · " : ""}{status}</span>
        </span>
        <span className={pillCls} data-testid={`kyc-status-${testKey}`}>{status}</span>
        {chevron}
      </summary>
      <dl className="mt-2 pl-11 grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-xs" data-testid={`kyc-doc-${testKey}-details`}>
        {details.map(([k, v]) => <React.Fragment key={k}><dt className="text-slate-400">{k}</dt><dd className="text-slate-700 min-w-0 break-words" data-testid={k === "On record" ? `kyc-${testKey}-value` : undefined}>{v}</dd></React.Fragment>)}
      </dl>
    </details>
  );
}

export function DigiLockerSummary({ dlDocs, labels }) {
  const order = Object.keys(labels);
  const docs = Object.values(dlDocs).sort((a, b) => order.indexOf(a.doc_type) - order.indexOf(b.doc_type));
  const latest = docs.map((d) => d.digilocker?.fetched_at).filter(Boolean).sort().pop();
  const names = docs.map((d) => labels[d.doc_type] || d.doc_type);
  return (
    <details className="group bg-white border border-slate-200 rounded-2xl p-4" data-testid="kyc-digilocker-info">
      <summary className={summaryCls}>
        <span className="h-9 w-9 rounded-xl bg-[#0A2540] text-[#D4AF37] grid place-items-center shrink-0"><Landmark size={16} /></span>
        <span className="flex-1 min-w-0">
          <span className="block text-sm font-bold text-[#0A2540]">DigiLocker</span>
          <span className="block text-xs text-slate-500 truncate" data-testid="kyc-digilocker-summary">
            {docs.length ? `Connected · ${names.join(" & ")} verified` : "Not used — documents were uploaded manually"}
          </span>
          {latest && <span className="block text-[11px] text-slate-400" data-testid="kyc-digilocker-last-fetched">Last fetched: {fmtDate(latest)}</span>}
        </span>
        {docs.length > 0 && chevron}
      </summary>
      {docs.length > 0 && (
        <ul className="mt-3 pl-12 space-y-1 text-xs text-slate-600" data-testid="kyc-digilocker-details">
          {docs.map((d) => (
            <li key={d.doc_type}>{labels[d.doc_type] || d.doc_type}{d.digilocker?.issuer ? ` · ${d.digilocker.issuer}` : ""}{d.digilocker?.fetched_at ? ` · fetched ${fmtDate(d.digilocker.fetched_at)}` : ""}</li>
          ))}
        </ul>
      )}
    </details>
  );
}

export function ProfessionalDetails({ kycBci, bank, levelDetail }) {
  const level = /Level\s+(\d+)/i.exec(levelDetail || "");
  const rows = [
    ["bci", ScrollText, "Bar Council enrolment", kycBci?.enrolment_number || "—", "Verified"],
    ["payout", CreditCard, "Payout account", bank?.masked || "—", "Verified"],
    ["level", BadgeCheck, "Lawyer level", level ? `Level ${level[1]}` : (levelDetail || "—"), "Assigned by Admin"],
  ];
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-4" data-testid="kyc-professional-details">
      <p className="text-[11px] uppercase tracking-widest font-bold text-slate-400 mb-2">Verified professional details</p>
      <ul className="divide-y divide-slate-100">
        {rows.map(([k, Icon, label, value, tag]) => (
          <li key={k} className="py-2.5 flex items-center gap-3 min-w-0" data-testid={`kyc-pro-${k}`}>
            <span className="h-8 w-8 rounded-lg bg-slate-100 text-[#0A2540] grid place-items-center shrink-0"><Icon size={15} /></span>
            <span className="flex-1 min-w-0">
              <span className="block text-xs text-slate-500">{label}</span>
              <span className="block text-sm font-bold text-[#0A2540] truncate" data-testid={`kyc-pro-${k}-value`}>{value}</span>
            </span>
            <span className={pillCls}>{tag}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
